import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Globe } from 'lucide-react';
import { useLanguageSwitch } from '../../i18n/hooks/useLanguageSwitch';
import { LANGUAGE_NAMES, SUPPORTED_LANGUAGES } from '../../i18n/constants/languages';
import type { SupportedLanguage } from '../../i18n/types/language';

const LanguageSwitcher = () => {
  const { currentLanguage, switchLanguage } = useLanguageSwitch();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const handleLanguageChange = async (lang: SupportedLanguage) => {
    setOpen(false);
    if (currentLanguage !== lang) {
      await switchLanguage(lang);
    }
  };

  return (
    <div ref={ref} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium text-slate-300 border border-white/10 bg-white/[0.03] hover:text-white hover:border-cyan-400/40 transition-all"
      >
        <Globe className="h-4 w-4 text-cyan-300" />
        <span>{LANGUAGE_NAMES[currentLanguage as SupportedLanguage]}</span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      <div
        className={`absolute left-0 md:left-auto md:right-0 top-full pt-2 w-44 z-50 transition-all duration-200 ${
          open ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-1'
        }`}
      >
        <div className="py-2 rounded-2xl border border-white/10 bg-slate-950/95 backdrop-blur-xl shadow-2xl">
          {SUPPORTED_LANGUAGES.map((lang) => {
            const active = currentLanguage === lang;
            return (
              <button
                key={lang}
                onClick={() => handleLanguageChange(lang)}
                className={`flex items-center justify-between w-full px-4 py-2 text-left text-sm transition-colors ${
                  active ? 'text-cyan-300 bg-cyan-400/10' : 'text-slate-300 hover:text-white hover:bg-white/5'
                }`}
              >
                {LANGUAGE_NAMES[lang]}
                {active && <Check className="h-4 w-4" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LanguageSwitcher;
